import { api, request } from './client';
import { ApiError } from './errors';
import type {
  AuthResponse,
  CourseDetail,
  CourseSummary,
  CreateGroupRoundInput,
  FeedItem,
  GroupRound,
  GuestPlayer,
  Friend,
  FriendRequests,
  Handicap,
  Hole,
  Paginated,
  PlayerProfile,
  PlayingPartner,
  ReactionToggle,
  JoinGroupRoundInput,
  RecordGroupHoleInput,
  RecordHoleScoreInput,
  Round,
  RoundComment,
  RoundStatus,
  RoundsTogether,
  RoundSummary,
  StartRoundInput,
  Tokens,
  UpdateProfileInput,
  User,
  UserSearchResult,
} from './types';

// ------------------------------------------------------------------ auth

export const authApi = {
  register: (input: { email: string; password: string; displayName: string }) =>
    request<AuthResponse>('/auth/register', { method: 'POST', body: input, auth: false }),

  login: (input: { email: string; password: string }) =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: input, auth: false }),

  google: (idToken: string) =>
    request<AuthResponse>('/auth/google', { method: 'POST', body: { idToken }, auth: false }),

  apple: (identityToken: string, authorizationCode?: string) =>
    request<AuthResponse>('/auth/apple', {
      method: 'POST',
      body: authorizationCode ? { identityToken, authorizationCode } : { identityToken },
      auth: false,
    }),

  refresh: (refreshToken: string) =>
    request<Tokens>('/auth/refresh', { method: 'POST', body: { refreshToken }, auth: false }),

  /** Requires a *valid* access token as well as the refresh token. */
  logout: (refreshToken: string) => api.post<void>('/auth/logout', { refreshToken }),

  me: () => api.get<User>('/auth/me'),
};

// ----------------------------------------------------------------- users

export const usersApi = {
  me: () => api.get<User>('/users/me'),

  updateMe: (input: UpdateProfileInput) => api.patch<User>('/users/me', input),

  handicap: () => api.get<Handicap>('/users/me/handicap'),
};

// --------------------------------------------------------------- courses

export const coursesApi = {
  list: (params: { city?: string; country?: string; page?: number; limit?: number } = {}) =>
    api.get<Paginated<CourseSummary>>('/courses', { query: params }),

  /**
   * Returns a bare array (not paginated) already sorted by distance — but *without*
   * a distance field. Compute it client-side with haversineMeters.
   * lng/lat bypass the ValidationPipe: omitting them yields a 500, not a 400.
   */
  nearby: (params: { lng: number; lat: number; radiusKm?: number; limit?: number }) =>
    api.get<CourseSummary[]>('/courses/nearby', { query: params }),

  get: (id: string) => api.get<CourseDetail>(`/courses/${id}`),

  hole: (courseId: string, holeNumber: number) =>
    api.get<Hole>(`/courses/${courseId}/holes/${holeNumber}`),
};

// ---------------------------------------------------------------- rounds

export const roundsApi = {
  list: (params: { status?: RoundStatus; page?: number; limit?: number } = {}) =>
    api.get<Paginated<RoundSummary>>('/rounds', { query: params }),

  get: (id: string) => api.get<Round>(`/rounds/${id}`),

  start: (input: StartRoundInput) => api.post<Round>('/rounds', input),

  /** Upsert of a single hole. Returns the whole round back. */
  recordHole: (roundId: string, holeNumber: number, input: RecordHoleScoreInput) =>
    api.patch<Round>(`/rounds/${roundId}/holes/${holeNumber}`, input),

  /** `asHolesPlayed` closes an 18 declared round as a 9 using the holes already filled. */
  complete: (roundId: string, asHolesPlayed?: 9 | 18) =>
    api.post<Round>(`/rounds/${roundId}/complete`, asHolesPlayed ? { asHolesPlayed } : {}),

  abandon: (roundId: string) => api.post<void>(`/rounds/${roundId}/abandon`),
};

// ---------------------------------------------------------------- social

/**
 * O jogo de outra pessoa (canvas 3a/3b).
 *
 * Prefixo `players` e não `users`: no servidor o prefixo `users` é dividido com a busca, cuja rota
 * `search` é literal, e um `:id` ali passaria a engoli-la dependendo da ordem de declaração.
 */
export const playersApi = {
  profile: (userId: string) => api.get<PlayerProfile>(`/players/${userId}`),

  rounds: (userId: string, params: { page?: number; limit?: number } = {}) =>
    api.get<Paginated<RoundSummary>>(`/players/${userId}/rounds`, {
      query: { status: 'completed', page: params.page ?? 1, limit: params.limit ?? 20 },
    }),

  roundsTogether: (userId: string) =>
    api.get<RoundsTogether>(`/players/${userId}/rounds-together`),
};

export const socialApi = {
  friends: () => api.get<Friend[]>('/friends'),

  requests: () => api.get<FriendRequests>('/friends/requests'),

  sendRequest: (userId: string) =>
    api.post<{ id: string; status: string }>('/friends/requests', { userId }),

  accept: (requestId: string) => api.post<void>(`/friends/requests/${requestId}/accept`),

  /** Recusar apaga o pedido: a pessoa pode ser convidada de novo depois. */
  decline: (requestId: string) => api.post<void>(`/friends/requests/${requestId}/decline`),

  removeFriend: (userId: string) => api.delete<void>(`/friends/${userId}`),

  /** Termo vazio devolve lista vazia — o app não sugere jogadores (canvas 1q). */
  search: (q: string) => api.get<UserSearchResult[]>('/users/search', { query: { q } }),

  feed: (params: { page?: number; limit?: number } = {}) =>
    api.get<Paginated<FeedItem>>('/feed', { query: params }),

  /** Alterna "Bom jogo". Uma reação por pessoa por volta, então reagir de novo desfaz. */
  toggleReaction: (roundId: string) =>
    api.post<ReactionToggle>(`/rounds/${roundId}/reactions`),

  comments: (roundId: string) => api.get<RoundComment[]>(`/rounds/${roundId}/comments`),

  addComment: (roundId: string, body: string) =>
    api.post<RoundComment>(`/rounds/${roundId}/comments`, { body }),

  deleteComment: (roundId: string, commentId: string) =>
    api.delete<void>(`/rounds/${roundId}/comments/${commentId}`),
};

// ----------------------------------------------------------- volta em grupo

export const groupRoundsApi = {
  /** Cria o flight **e** a volta do dono numa chamada — é ela que entra no handicap dele. */
  create: (input: CreateGroupRoundInput) => api.post<GroupRound>('/group-rounds', input),

  get: (id: string) => api.get<GroupRound>(`/group-rounds/${id}`),

  /** Com quem você já jogou, e quantas vezes — a fonte de "14 voltas juntos" (canvas 1q, 1u). */
  partners: () => api.get<PlayingPartner[]>('/group-rounds/partners'),

  /**
   * Resolve o flight a partir da volta. Devolve `null` quando a volta é individual — não é erro,
   * é a resposta: a maioria das voltas não tem grupo, e quem pergunta está justamente descobrindo
   * qual dos dois scorecards abrir.
   */
  byRound: async (roundId: string): Promise<GroupRound | null> => {
    try {
      return await api.get<GroupRound>(`/group-rounds/by-round/${roundId}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /** Marca o buraco inteiro, de todos, numa chamada só (canvas 1s). */
  /** Entrar num flight em que você foi incluído — abre o seu cartão (canvas 2a). */
  join: (groupRoundId: string, input: JoinGroupRoundInput) =>
    api.post<GroupRound>(`/group-rounds/${groupRoundId}/join`, input),

  recordHole: (groupRoundId: string, holeNumber: number, input: RecordGroupHoleInput) =>
    api.patch<GroupRound>(`/group-rounds/${groupRoundId}/holes/${holeNumber}`, input),

  complete: (groupRoundId: string, asHolesPlayed?: 9 | 18) =>
    api.post<GroupRound>(
      `/group-rounds/${groupRoundId}/complete`,
      asHolesPlayed ? { asHolesPlayed } : {},
    ),
};

export const guestPlayersApi = {
  list: () => api.get<GuestPlayer[]>('/guest-players'),

  /** "Salvar para as próximas — fica na sua lista, sem criar conta para ele." */
  create: (input: { displayName: string; handicapIndex: number | null }) =>
    api.post<GuestPlayer>('/guest-players', input),

  remove: (id: string) => api.delete<void>(`/guest-players/${id}`),
};
